'use client';

import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewContent, type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { KeyboardEvent } from 'react';

interface EmotionTagOptions {
  HTMLAttributes: Record<string, unknown>;
}

function EmotionTagNodeView({ node, deleteNode }: NodeViewProps) {
  const tagBackgroundColor = node.attrs.tagBackgroundColor || node.attrs.color || 'rgba(255, 107, 107, 0.24)';
  const tagTextColor = node.attrs.tagTextColor || '#FF6B6B';
  const emotionTextBackgroundColor =
    node.attrs.emotionTextBackgroundColor || node.attrs.color || 'rgba(255, 107, 107, 0.16)';
  const emotionLabel = node.attrs.emotionName || node.attrs.emotionId;

  return (
    <NodeViewWrapper
      as="span"
      data-emotion-tag=""
      data-emotion-id={node.attrs.emotionId}
      className="group mx-1 rounded-8 py-[6px] pr-2 align-baseline [-webkit-box-decoration-break:clone] [box-decoration-break:clone]"
      style={{ backgroundColor: emotionTextBackgroundColor }}
    >
      <span
        data-emotion-label=""
        contentEditable={false}
        className="mr-2 cursor-pointer select-none whitespace-nowrap rounded-8 border border-system-white08 px-[13px] py-[5px] font-semibold text-sm leading-[1.4]"
        style={{ backgroundColor: tagBackgroundColor, color: tagTextColor }}
      >
        <span>{emotionLabel}</span>
        <span
          data-emotion-delete=""
          className="ml-1 hidden cursor-pointer group-data-[emotion-active=true]:inline"
          style={{ color: tagTextColor }}
        >
          <span className="opacity-40">|</span>
          <span className="-mr-2 ml-0.5 px-1 text-[10px]">✕</span>
        </span>
      </span>

      <span className="text-sm text-white leading-[30px]">
        <NodeViewContent
          as="span"
          data-emotion-content=""
          className="whitespace-pre-wrap break-all bg-transparent text-sm text-white leading-[30px] outline-none"
          onKeyDown={(event: KeyboardEvent<HTMLSpanElement>) => {
            if (event.key !== 'Backspace') return;
            if (node.content.size === 0) {
              event.preventDefault();
              event.stopPropagation();
              deleteNode();
            }
          }}
        />
      </span>
    </NodeViewWrapper>
  );
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emotionTag: {
      insertEmotionTag: (attrs: {
        emotionId: string;
        emotionName: string;
        tagBackgroundColor: string;
        tagTextColor: string;
        emotionTextBackgroundColor: string;
        text?: string;
      }) => ReturnType;
    };
  }
}

export const EmotionTag = Node.create<EmotionTagOptions>({
  name: 'emotionTag',
  group: 'inline',
  inline: true,
  selectable: true,
  draggable: false,
  content: 'inline*',
  marks: '',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      emotionId: { default: '' },
      emotionName: { default: '' },
      tagBackgroundColor: { default: 'rgba(255, 107, 107, 0.24)' },
      tagTextColor: { default: '#FF6B6B' },
      emotionTextBackgroundColor: { default: 'rgba(255, 107, 107, 0.16)' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-emotion-tag]',
        contentElement: '[data-emotion-content]',
      },
    ];
  },

  renderHTML({ node }) {
    const tagBackgroundColor = node.attrs.tagBackgroundColor || node.attrs.color || 'rgba(255, 107, 107, 0.24)';
    const tagTextColor = node.attrs.tagTextColor || '#FF6B6B';
    const emotionTextBackgroundColor =
      node.attrs.emotionTextBackgroundColor || node.attrs.color || 'rgba(255, 107, 107, 0.16)';
    return [
      'span',
      mergeAttributes({
        ...this.options.HTMLAttributes,
        'data-emotion-tag': '',
        'data-emotion-id': node.attrs.emotionId,
        class:
          'group rounded-8 py-[6px] pr-2 align-baseline [box-decoration-break:clone] [-webkit-box-decoration-break:clone]',
        style: `background-color: ${emotionTextBackgroundColor}`,
      }),
      [
        'span',
        {
          'data-emotion-label': '',
          contenteditable: 'false',
          class:
            'mr-2 cursor-pointer select-none whitespace-nowrap rounded-8 border border-system-white08 px-[13px] py-[5px] text-sm font-semibold leading-[1.4]',
          style: `background-color: ${tagBackgroundColor}; color: ${tagTextColor}`,
        },
        node.attrs.emotionName || node.attrs.emotionId,
      ],
      [
        'span',
        {
          class: 'text-sm leading-[30px] text-white',
        },
        [
          'span',
          {
            'data-emotion-content': '',
            class: 'whitespace-pre-wrap break-all bg-transparent text-sm leading-[30px] text-white',
          },
          0,
        ],
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmotionTagNodeView);
  },

  addCommands() {
    return {
      insertEmotionTag:
        (attrs) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs,
            })
            .run();
        },
    };
  },
});
